import { useState, useMemo, useEffect, useCallback } from 'react'
import {
  FileText,
  Calendar as CalendarIcon,
  MessageSquare,
  ShoppingCart,
  CalendarCheck,
  MessageCircle,
  HelpCircle,
  Mic,
  Loader2,
} from 'lucide-react'
import { useTranscripts } from '../features/transcripts/hooks/useTranscripts'
import { useDeviceTeams } from '../features/transcripts/hooks/useDeviceTeams'
import { useTeams } from '../features/transcripts/hooks/useTeams'
import { AISummary } from '../features/transcripts/components/AISummary'
import { TeamFilterPills, UNASSIGNED_TEAM_ID } from '../features/transcripts/components/TeamFilterPills'
import { Calendar } from '../features/transcripts/components/Calendar'
import { TranscriptDetailDrawer } from '../features/transcripts/components/TranscriptDetailDrawer'
import { useSelectedPropertyId } from '../store/propertyStore'
import { LiveIndicator } from '../components/LiveIndicator'
import type { Transcript, LiveTranscriptPreview } from '../types/transcript.types'

type TranscriptTab = 'summary' | 'transcript'

function formatTime(dateString: string | null): string {
  if (!dateString) return ''
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return ''
    const hours = date.getHours()
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const seconds = String(date.getSeconds()).padStart(2, '0')
    const period = hours >= 12 ? 'PM' : 'AM'
    const displayHours = String(hours % 12 || 12).padStart(2, '0')
    return `${displayHours}:${minutes}:${seconds} ${period}`
  } catch { return '' }
}

function formatDate(date: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
}

function getIntentStyle(intentType: Transcript['intent_type']): {
  bg: string; border: string; iconBg: string; iconColor: string; Icon: typeof ShoppingCart; label: string
} {
  switch (intentType) {
    case 'order': return { bg: 'bg-green-50', border: 'border-l-4 border-green-500', iconBg: 'bg-green-100', iconColor: 'text-green-700', Icon: ShoppingCart, label: 'Order' }
    case 'reservation': return { bg: 'bg-blue-50', border: 'border-l-4 border-blue-500', iconBg: 'bg-blue-100', iconColor: 'text-blue-700', Icon: CalendarCheck, label: 'Reservation' }
    case 'feedback': return { bg: 'bg-yellow-50', border: 'border-l-4 border-yellow-500', iconBg: 'bg-yellow-100', iconColor: 'text-yellow-700', Icon: MessageCircle, label: 'Feedback' }
    default: return { bg: 'bg-gray-50', border: 'border-l-4 border-gray-300', iconBg: 'bg-gray-100', iconColor: 'text-gray-500', Icon: HelpCircle, label: 'General' }
  }
}

function LiveTranscriptPreviewBox({ preview }: { preview: LiveTranscriptPreview }): JSX.Element {
  const sourceLabel = preview.device_id != null ? `Device ${preview.device_id}` : preview.username ? preview.username : preview.device_user_id != null ? `User ${preview.device_user_id}` : 'Live channel'
  const conf = preview.confidence != null ? `${Math.round(preview.confidence * 100)}% conf.` : null
  return (
    <div className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100">
          <Mic size={18} className="text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <span className="text-[14px] font-semibold text-amber-800">Live transcript</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
              <Loader2 size={12} className="animate-spin" /> In progress...
            </span>
            {conf && <span className="text-[11px] text-gray-400">{conf}</span>}
          </div>
          <p className="text-xs text-gray-500">{sourceLabel} · session {preview.device_session}</p>
          <p className={`mt-2 text-[15px] leading-relaxed ${preview.isUrdu ? 'italic text-amber-600' : 'text-gray-900'}`}>
            {preview.isUrdu ? 'User is speaking in Urdu' : (preview.transcript_text.trim() || '...')}
          </p>
        </div>
      </div>
    </div>
  )
}

export function TranscriptsPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<TranscriptTab>('transcript')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDay, setSelectedDay] = useState(() => {
    const t = new Date()
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`
  })
  const selectedPropertyId = useSelectedPropertyId()
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [selectedTranscript, setSelectedTranscript] = useState<Transcript | null>(null)
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null)

  const { transcripts, livePreviews, loading, error, streamStatus } = useTranscripts(selectedDay, undefined, selectedPropertyId ?? undefined)
  const { deviceTeams, onlineTeamIds } = useDeviceTeams(selectedPropertyId ?? undefined)
  const teams = useTeams(selectedPropertyId ?? undefined)
  const selectedDate = new Date(selectedDay + 'T00:00:00')

  useEffect(() => {
    setSelectedTeamId(null)
  }, [selectedPropertyId])

  const livePreviewList = useMemo(() => Array.from(livePreviews.values()), [livePreviews])

  const showUnassigned = useMemo(
    () =>
      transcripts.some((t) => (deviceTeams[t.device_id] ?? null) === null) ||
      livePreviewList.some((p) => (p.device_id != null ? deviceTeams[p.device_id] ?? null : null) === null),
    [transcripts, livePreviewList, deviceTeams],
  )

  const matchesTeam = useCallback(
    (teamId: number | null): boolean => {
      if (selectedTeamId === null) return true
      return selectedTeamId === UNASSIGNED_TEAM_ID ? teamId === null : teamId === selectedTeamId
    },
    [selectedTeamId],
  )

  const filteredTranscripts = useMemo(() => {
    const query = searchQuery.toLowerCase()
    return transcripts.filter((t) => {
      if (!matchesTeam(deviceTeams[t.device_id] ?? null)) return false
      if (!query) return true
      const name = t.username ?? ''
      return t.transcript_text.toLowerCase().includes(query) || `device ${t.device_id}`.toLowerCase().includes(query) || name.toLowerCase().includes(query)
    })
  }, [transcripts, searchQuery, matchesTeam, deviceTeams])

  const filteredLivePreviews = useMemo(
    () => livePreviewList.filter((p) => matchesTeam(p.device_id != null ? deviceTeams[p.device_id] ?? null : null)),
    [livePreviewList, matchesTeam, deviceTeams],
  )

  if (loading) {
    return <div className="py-12 text-center"><p className="text-[14px] text-gray-400">Loading transcripts...</p></div>
  }

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <FileText size={20} className="text-brand-green" />
            <h1 className="text-[22px] font-bold leading-tight text-gray-900 sm:text-[28px]">Transcript Details</h1>
          </div>
          <div className="mt-2 flex items-center gap-3 text-[14px] text-gray-500">
            <div className="relative">
              <button onClick={() => setCalendarOpen((v) => !v)} className={`flex items-center gap-1.5 transition-colors ${calendarOpen ? 'text-brand-green' : 'hover:text-gray-700'}`} type="button">
                <CalendarIcon size={14} />
                <span>{formatDate(selectedDate)}</span>
              </button>
              {calendarOpen && (
                <div className="absolute left-0 top-full z-40 mt-2">
                  <Calendar value={selectedDay} onChange={setSelectedDay} onClose={() => setCalendarOpen(false)} />
                </div>
              )}
            </div>
            <LiveIndicator status={streamStatus} />
          </div>
        </div>
        {activeTab === 'transcript' && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search transcripts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 w-full rounded-lg border border-[#E5E7EB] bg-white px-3 text-[14px] text-gray-700 outline-none focus:border-gray-400 sm:w-auto"
            />
          </div>
        )}
      </div>

      <div className="mb-6 flex gap-2 border-b border-[#E5E7EB]">
        <button onClick={() => setActiveTab('summary')} className={`border-b-2 px-4 py-2 text-[14px] transition-colors ${activeTab === 'summary' ? 'border-brand-green font-semibold text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>AI summary</button>
        <button onClick={() => setActiveTab('transcript')} className={`border-b-2 px-4 py-2 text-[14px] transition-colors ${activeTab === 'transcript' ? 'border-brand-green font-semibold text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>Transcript</button>
      </div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-[14px] text-red-600">{error}</div>}

      {activeTab === 'summary' && <AISummary day={selectedDay} />}

      {activeTab === 'transcript' && (
        <div>
          <TeamFilterPills
            teams={teams}
            selectedTeamId={selectedTeamId}
            onSelect={setSelectedTeamId}
            showUnassigned={showUnassigned}
            onlineTeamIds={onlineTeamIds}
          />
          {filteredLivePreviews.length > 0 && (
            <div className="mb-3 space-y-2">
              {filteredLivePreviews.map((preview) => (
                <LiveTranscriptPreviewBox key={preview.device_session} preview={preview} />
              ))}
            </div>
          )}
          {filteredTranscripts.length === 0 && filteredLivePreviews.length === 0 ? (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                <MessageSquare size={32} className="text-gray-400" />
              </div>
              <p className="text-[14px] text-gray-500">{searchQuery ? 'No transcripts match your search.' : selectedTeamId !== null ? 'No transcripts for this team.' : 'No transcripts found for today.'}</p>
            </div>
          ) : filteredTranscripts.length > 0 ? (
            <div className="space-y-2">
              {filteredTranscripts.map((transcript) => {
                const style = getIntentStyle(transcript.intent_type)
                const IntentIcon = style.Icon
                return (
                  <div key={transcript.id}
                    className={`flex cursor-pointer items-start gap-4 rounded-xl p-4 transition-all hover:ring-1 hover:ring-brand-green/30 ${style.bg} ${style.border}`}
                    onClick={() => setSelectedTranscript(transcript)}
                    role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedTranscript(transcript) } }}>
                    <div className={`mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${style.iconBg}`}>
                      <IntentIcon size={18} className={style.iconColor} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="text-[14px] font-bold text-gray-900">{transcript.username ?? `Device ${transcript.device_id}`}</span>
                        {transcript.intent_type && (
                          <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${style.iconBg} ${style.iconColor}`}>{style.label}</span>
                        )}
                      </div>
                      <p className="text-[14px] leading-relaxed text-gray-700">{transcript.transcript_text}</p>
                      <div className="mt-2">
                        <span className="inline-flex items-center rounded-full border border-[#E5E7EB] bg-white px-2 py-0.5 text-[10px] text-gray-500">
                          Device {transcript.device_id}
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 text-right">
                      <span className="text-[13px] text-gray-400">{formatTime(transcript.created_at)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>
      )}

      <TranscriptDetailDrawer transcript={selectedTranscript} onClose={() => setSelectedTranscript(null)} />
    </div>
  )
}
